import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Search, Loader2, Calendar as CalendarIcon, Tv, Tag, Filter, X, Download, Maximize2, Minimize2, Newspaper, Play } from "lucide-react";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import Highlight from "../components/Highlight";
import { toast } from "sonner";

export default function SearchPage() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [channel, setChannel] = useState("");
  const [dateFrom, setDateFrom] = useState(null); // Date | null
  const [dateTo, setDateTo] = useState(null);
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);

  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);

  const [results, setResults] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // Map of video_id -> { fulltext: string, loading: boolean }
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("/keywords/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/channels").then((r) => setChannels(r.data)).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (category) params.category = category;
    if (channel) params.channel = channel;
    if (dateFrom) params.date_from = format(dateFrom, "yyyy-MM-dd");
    if (dateTo) params.date_to = format(dateTo, "yyyy-MM-dd");
    return params;
  }, [q, category, channel, dateFrom, dateTo]);

  const runSearch = useCallback(async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const { data } = await api.get("/search", { params: { ...buildParams(), skip: 0, limit: 50 } });
      setResults(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE}/search/export?${new URLSearchParams(buildParams()).toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const disposition = res.headers.get("content-disposition") || "";
      const m = disposition.match(/filename="([^"]+)"/);
      a.href = url;
      a.download = m ? m[1] : "gardamedia-search.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export berhasil diunduh");
    } catch (e) {
      toast.error("Gagal export: " + (e.message || "error"));
    } finally {
      setExporting(false);
    }
  };

  const reset = () => {
    setQ(""); setCategory(""); setChannel(""); setDateFrom(null); setDateTo(null);
    setResults({ total: 0, items: [] });
    setHasSearched(false);
    setExpanded({});
  };

  const toggleExpand = async (r) => {
    const id = r.video_id;
    const current = expanded[id];
    if (current && current.fulltext) {
      // Already loaded → collapse
      setExpanded((e) => {
        const next = { ...e };
        delete next[id];
        return next;
      });
      return;
    }
    setExpanded((e) => ({ ...e, [id]: { fulltext: "", loading: true } }));
    try {
      const { data } = await api.get(`/transcripts/by-video/${r.video_id}`);
      const fulltext = data?.transcript?.full_text || "";
      setExpanded((e) => ({ ...e, [id]: { fulltext, loading: false } }));
    } catch (err) {
      toast.error(formatApiError(err));
      setExpanded((e) => {
        const next = { ...e };
        delete next[id];
        return next;
      });
    }
  };

  return (
    <div className="grid md:grid-cols-12 min-h-screen" data-testid="search-page">
      {/* Filters */}
      <aside className="md:col-span-3 border-r border-slate-200 bg-white px-4 py-5 space-y-5">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <p className="text-sm font-semibold">Filter</p>
          {(category || channel || dateFrom || dateTo) && (
            <button onClick={reset} className="ml-auto text-xs text-slate-500 hover:text-slate-900 underline" data-testid="filter-reset">
              Reset
            </button>
          )}
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-600 flex items-center gap-1"><Tag className="h-3 w-3" /> Kategori</Label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="mt-1 w-full rounded-sm border border-slate-300 bg-white text-sm px-2 py-1.5 focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:outline-none"
            data-testid="filter-category"
          >
            <option value="">Semua kategori</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-600 flex items-center gap-1"><Tv className="h-3 w-3" /> Channel</Label>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="mt-1 w-full rounded-sm border border-slate-300 bg-white text-sm px-2 py-1.5"
            data-testid="filter-channel"
          >
            <option value="">Semua channel</option>
            {channels.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-600 flex items-center gap-1"><CalendarIcon className="h-3 w-3" /> Tanggal</Label>
          <div className="mt-1 space-y-2">
            <Popover open={fromOpen} onOpenChange={setFromOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between rounded-sm border border-slate-300 bg-white text-sm px-2.5 py-1.5 hover:border-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                  data-testid="filter-date-from-trigger"
                >
                  <span className={dateFrom ? "text-slate-900" : "text-slate-400"}>
                    {dateFrom ? format(dateFrom, "d MMM yyyy", { locale: idLocale }) : "Dari"}
                  </span>
                  <CalendarIcon className="h-3.5 w-3.5 text-slate-500" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-sm border-slate-200" align="start">
                <Calendar
                  mode="single" selected={dateFrom} locale={idLocale}
                  onSelect={(d) => { setDateFrom(d); setFromOpen(false); }}
                  disabled={(d) => dateTo ? d > dateTo : d > new Date()}
                  initialFocus
                />
                {dateFrom && (
                  <div className="border-t border-slate-200 px-3 py-2 flex justify-end">
                    <button type="button" onClick={() => { setDateFrom(null); setFromOpen(false); }} className="text-xs text-slate-500 hover:text-slate-900 underline">Kosongkan</button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
            <Popover open={toOpen} onOpenChange={setToOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between rounded-sm border border-slate-300 bg-white text-sm px-2.5 py-1.5 hover:border-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                  data-testid="filter-date-to-trigger"
                >
                  <span className={dateTo ? "text-slate-900" : "text-slate-400"}>
                    {dateTo ? format(dateTo, "d MMM yyyy", { locale: idLocale }) : "Sampai"}
                  </span>
                  <CalendarIcon className="h-3.5 w-3.5 text-slate-500" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-sm border-slate-200" align="start">
                <Calendar
                  mode="single" selected={dateTo} locale={idLocale}
                  onSelect={(d) => { setDateTo(d); setToOpen(false); }}
                  disabled={(d) => (dateFrom ? d < dateFrom : false) || d > new Date()}
                  initialFocus
                />
                {dateTo && (
                  <div className="border-t border-slate-200 px-3 py-2 flex justify-end">
                    <button type="button" onClick={() => { setDateTo(null); setToOpen(false); }} className="text-xs text-slate-500 hover:text-slate-900 underline">Kosongkan</button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <Button
          onClick={runSearch}
          disabled={loading}
          className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 text-white shadow-none"
          data-testid="filter-apply-button"
        >
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Terapkan Filter
        </Button>
      </aside>

      {/* Results */}
      <section className="md:col-span-9 px-6 py-6">
        <header className="mb-4">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Intelligence Search</p>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Cari Transkrip Berita</h1>
          <p className="text-sm text-slate-500 mt-1">Ketik kata atau frasa (mis. "Panglima TNI", "Kopassus").</p>
        </header>

        <form
          onSubmit={(e) => { e.preventDefault(); runSearch(); }}
          className="bg-white border border-slate-200 rounded-sm flex items-center px-3"
          data-testid="search-form"
        >
          <Search className="h-4 w-4 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='Masukkan kata kunci, contoh: "Panglima TNI"'
            className="flex-1 bg-transparent text-sm px-3 py-3 focus:outline-none"
            data-testid="search-input"
          />
          {q && (
            <button type="button" onClick={() => setQ("")} className="text-slate-400 hover:text-slate-700">
              <X className="h-4 w-4" />
            </button>
          )}
          <Button type="submit" disabled={loading} size="sm" className="ml-2 rounded-sm bg-slate-900 hover:bg-slate-800 text-white shadow-none" data-testid="search-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cari"}
          </Button>
        </form>

        <div className="mt-6">
          {!hasSearched && (
            <div className="text-center py-16 text-sm text-slate-500" data-testid="search-idle">
              <Search className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              Masukkan keyword di atas atau pilih kategori untuk mulai mencari.
            </div>
          )}

          {hasSearched && !loading && results.items.length === 0 && (
            <div className="text-center py-16 text-sm text-slate-500" data-testid="search-empty">
              Tidak ditemukan transkrip yang cocok.
            </div>
          )}

          {results.items.length > 0 && (
            <div className="flex items-center justify-between mb-3" data-testid="search-result-summary">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold" data-testid="search-result-count">
                {results.total} hasil ditemukan
              </p>
              <Button
                onClick={exportCsv}
                disabled={exporting}
                variant="outline"
                size="sm"
                className="rounded-sm border-slate-300 hover:bg-slate-900 hover:text-white hover:border-slate-900"
                data-testid="search-export-button"
              >
                {exporting ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-2" />}
                Export CSV
              </Button>
            </div>
          )}

          <ul className="space-y-3">
            {results.items.map((r) => {
              const exp = expanded[r.video_id];
              const isExpanded = !!(exp && exp.fulltext);
              const isLoading = !!(exp && exp.loading);
              return (
                <li
                  key={r.transcript_id}
                  className="bg-white border border-slate-200 rounded-sm p-4 hover:border-slate-400 transition-colors"
                  data-testid={`search-result-${r.video_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                        <span className="font-medium text-slate-900 truncate">{r.channel_name}</span>
                        <span>·</span>
                        <span className="font-mono">{r.recording_date || "—"}</span>
                      </div>
                      <p className="font-semibold text-sm truncate" title={r.file_name}>{r.file_name}</p>
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => toggleExpand(r)}
                        disabled={isLoading}
                        className="inline-flex items-center text-xs font-medium border border-slate-300 hover:bg-slate-100 rounded-sm px-2.5 py-1.5 transition-colors disabled:opacity-50"
                        title={isExpanded ? "Minimize transkrip" : "Maximize transkrip penuh"}
                        data-testid={`toggle-fulltext-${r.video_id}`}
                      >
                        {isLoading ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : isExpanded ? (
                          <><Minimize2 className="h-3.5 w-3.5 mr-1" /> Minimize</>
                        ) : (
                          <><Maximize2 className="h-3.5 w-3.5 mr-1" /> Maximize</>
                        )}
                      </button>
                      <Link
                        to={`/transcript/${r.video_id}`}
                        className="inline-flex items-center text-xs font-medium border border-slate-300 hover:bg-slate-900 hover:text-white hover:border-slate-900 rounded-sm px-3 py-1.5 transition-colors"
                        data-testid={`open-detail-${r.video_id}`}
                      >
                        Buka Detail
                      </Link>
                    </div>
                  </div>

                  {isExpanded ? (
                    <div className="mt-3" data-testid={`result-fulltext-${r.video_id}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                          Transkrip penuh · {exp.fulltext.length.toLocaleString("id-ID")} karakter
                        </p>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 max-h-[420px] overflow-y-auto">
                        <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
                          <Highlight text={exp.fulltext} query={q} keywords={r.matched_keywords} />
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-slate-700 leading-relaxed" data-testid={`result-snippet-${r.video_id}`}>
                      <Highlight text={r.snippet} query={q} keywords={r.matched_keywords} />
                    </p>
                  )}

                  {(r.matching_news && r.matching_news.length > 0 && q) && (
                    <div className="mt-3 border-l-2 border-amber-400 pl-3" data-testid={`matching-news-${r.video_id}`}>
                      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1">
                        <Newspaper className="h-3 w-3" />
                        Ditemukan di {r.matching_news.length} berita
                        {r.news_items_total > r.matching_news.length && (
                          <span className="text-slate-400 font-normal normal-case"> dari total {r.news_items_total} berita</span>
                        )}
                      </p>
                      <ul className="space-y-1.5">
                        {r.matching_news.slice(0, 5).map((n) => {
                          const mn = Math.floor(n.start_time / 60);
                          const sec = Math.floor(n.start_time % 60);
                          const ts = `${String(mn).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
                          return (
                            <li key={n.id} className="text-sm" data-testid={`matched-news-${n.id}`}>
                              <Link
                                to={`/transcript/${r.video_id}?news=${n.id}`}
                                className="group flex items-start gap-2 hover:bg-amber-50 rounded-sm p-1.5 -ml-1.5 transition-colors"
                              >
                                <span className="shrink-0 inline-flex h-5 px-1.5 items-center rounded-sm bg-slate-900 text-white font-mono text-[10px] font-bold">
                                  #{n.ordinal}
                                </span>
                                <span className="shrink-0 font-mono text-[11px] text-slate-500 mt-0.5">{ts}</span>
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-slate-900 group-hover:text-amber-800 leading-snug">{n.title}</p>
                                  {n.snippet && (
                                    <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">
                                      <Highlight text={n.snippet} query={q} keywords={n.matched_keywords} />
                                    </p>
                                  )}
                                </div>
                                <Play className="h-3 w-3 text-slate-400 group-hover:text-amber-600 shrink-0 mt-1" />
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                      {r.matching_news.length > 5 && (
                        <p className="text-[11px] text-slate-500 mt-1.5">
                          +{r.matching_news.length - 5} berita lain — buka detail untuk lihat semua
                        </p>
                      )}
                    </div>
                  )}

                  {(r.matched_keywords?.length > 0) && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {r.matched_keywords.map((k) => (
                        <Badge key={k} className="rounded-sm bg-amber-100 text-amber-900 hover:bg-amber-200 border-none font-medium">
                          {k}
                        </Badge>
                      ))}
                      {r.categories?.map((c) => (
                        <Badge key={c} variant="outline" className="rounded-sm border-slate-300 text-slate-600 font-normal">
                          {c}
                        </Badge>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    </div>
  );
}
